import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { MetricsMargin } from '@/services/adminMetricsService';
import { cn } from '@/lib/utils';

interface MarginCardProps {
  data: MetricsMargin;
  formatCents: (cents: number, currency: 'BRL' | 'EUR') => string;
}

/**
 * Gross margin summary card.
 * Margin coloured by sign: success when positive, destructive when negative.
 */
export function MarginCard({ data, formatCents }: MarginCardProps) {
  const { t } = useTranslation();
  const marginPositive = data.gross_margin_normalized_cents >= 0;

  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.margin.mrr_normalized')}</dt>
        <dd className="mt-1 font-semibold tabular-nums">
          {formatCents(data.normalized_mrr_cents, 'EUR')}
          <span className="ml-1.5">
            <Badge variant="outline" className="text-xs">
              {t('admin.metrics.margin.aprox')}
            </Badge>
          </span>
        </dd>
      </div>

      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.margin.llm_cost_production')}</dt>
        <dd className="mt-1 font-semibold tabular-nums">
          ${data.llm_cost_production_usd} USD
          <div className="text-xs text-muted-foreground">
            ≈ {formatCents(data.llm_cost_production_normalized_cents, 'EUR')}
          </div>
        </dd>
      </div>

      <div>
        <dt className="text-muted-foreground">{t('admin.metrics.margin.gross_margin')}</dt>
        <dd
          className={cn(
            'mt-1 text-xl font-bold tabular-nums',
            marginPositive ? 'text-success' : 'text-destructive'
          )}
        >
          {marginPositive ? '+' : ''}
          {formatCents(data.gross_margin_normalized_cents, 'EUR')}
        </dd>
      </div>

      <div className="col-span-full">
        <dt className="text-muted-foreground">{t('admin.metrics.margin.llm_cost_preview')}</dt>
        <dd className="mt-0.5 text-xs text-muted-foreground tabular-nums">
          ${data.llm_cost_preview_usd} USD ({t('admin.metrics.margin.preview_label')})
        </dd>
      </div>
    </dl>
  );
}
